import { subject } from '@casl/ability';
import {
    ChartKind,
    isVizCartesianChartConfig,
    isVizPieChartConfig,
    isVizTableConfig,
    type DashboardSqlChartTile,
} from '@lightdash/common';
import { Box, Menu } from '@mantine-8/core';
import {
    IconAlertCircle,
    IconFilePencil,
    IconTableExport,
} from '@tabler/icons-react';
import {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
    type FC,
} from 'react';
import { useParams } from 'react-router';
import { useSavedSqlChartResults } from '../../features/sqlRunner/hooks/useSavedSqlChartResults';
import useDashboardFiltersForTile from '../../hooks/dashboard/useDashboardFiltersForTile';
import useSearchParams from '../../hooks/useSearchParams';
import useApp from '../../providers/App/useApp';
import useDashboardContext from '../../providers/Dashboard/useDashboardContext';
import useDashboardTileStatusContext from '../../providers/Dashboard/useDashboardTileStatusContext';
import LinkMenuItem from '../common/LinkMenuItem';
import MantineIcon from '../common/MantineIcon';
import SuboptimalState from '../common/SuboptimalState/SuboptimalState';
import ChartView from '../DataViz/visualizations/ChartView';
import { Table } from '../DataViz/visualizations/Table';
import ExportDataModal from './ExportDataModal';
import TileBase from './TileBase';

interface Props extends Pick<
    React.ComponentProps<typeof TileBase>,
    'tile' | 'onEdit' | 'onDelete' | 'isEditMode'
> {
    tile: DashboardSqlChartTile;
    minimal?: boolean;
}

/**
 * TODO
 * Handle minimal mode
 * handle tabs
 */
const DashboardOptions = memo(
    ({
        isEditMode,
        projectUuid,
        slug,
    }: {
        isEditMode: boolean;
        projectUuid: string;
        slug: string;
    }) => (
        <LinkMenuItem
            leftSection={<MantineIcon icon={IconFilePencil} />}
            href={`/projects/${projectUuid}/sql-runner/${slug}/edit`}
            disabled={isEditMode}
            target="_blank"
        >
            Edit SQL chart
        </LinkMenuItem>
    ),
);

const SqlChartTile: FC<Props> = ({ tile, isEditMode, ...rest }) => {
    const { user } = useApp();
    const { projectUuid, dashboardUuid } = useParams<{
        projectUuid: string;
        dashboardUuid: string;
    }>();
    const context = useSearchParams('context') || undefined;
    const savedSqlUuid = tile.properties.savedSqlUuid || undefined;
    const [isDataExportModalOpen, setIsDataExportModalOpen] = useState(false);
    const canManageSqlRunner = user.data?.ability?.can(
        'manage',
        subject('SqlRunner', {
            organizationUuid: user.data?.organizationUuid,
            projectUuid,
        }),
    );
    const updateSqlChartTilesMetadata = useDashboardTileStatusContext(
        (c) => c.updateSqlChartTilesMetadata,
    );
    const parameters = useDashboardContext((c) => c.parameterValues);
    const markTileScreenshotReady = useDashboardTileStatusContext(
        (c) => c.markTileScreenshotReady,
    );
    const markTileScreenshotErrored = useDashboardTileStatusContext(
        (c) => c.markTileScreenshotErrored,
    );
    const dashboardFilters = useDashboardFiltersForTile(tile.uuid);

    const closeDataExportModal = useCallback(
        () => setIsDataExportModalOpen(false),
        [],
    );

    const {
        chartQuery: {
            data: chartData,
            isLoading: isChartLoading,
            error: chartError,
        },
        chartResultsQuery: {
            data: chartResultsData,
            isLoading: isChartResultsLoading,
            error: chartResultsError,
            isFetching: isChartResultsFetching,
        },
        getDownloadQueryUuid,
    } = useSavedSqlChartResults({
        projectUuid,
        savedSqlUuid,
        context,
        dashboardUuid,
        tileUuid: tile.uuid,
        dashboardFilters,
        dashboardSorts: [],
        parameters,
    });

    // Charts in Dashboard shouldn't have animation
    const specWithoutAnimation = useMemo(() => {
        if (!chartResultsData?.chartSpec) return chartResultsData?.chartSpec;
        return {
            ...chartResultsData.chartSpec,
            animation: false,
        };
    }, [chartResultsData?.chartSpec]);

    // Update SQL chart columns in the dashboard context
    useEffect(() => {
        if (chartResultsData?.originalColumns) {
            updateSqlChartTilesMetadata(tile.uuid, {
                columns: Object.values(chartResultsData.originalColumns),
            });
        }
    }, [
        chartResultsData?.originalColumns,
        tile.uuid,
        updateSqlChartTilesMetadata,
    ]);

    useEffect(() => {
        if (!savedSqlUuid) {
            markTileScreenshotErrored(tile.uuid);
            return;
        }
        if (chartError || chartResultsError) {
            markTileScreenshotErrored(tile.uuid);
            return;
        }
        if (!isChartLoading && !isChartResultsLoading && chartResultsData) {
            markTileScreenshotReady(tile.uuid);
        }
    }, [
        savedSqlUuid,
        isChartLoading,
        isChartResultsLoading,
        chartResultsData,
        chartError,
        chartResultsError,
        tile.uuid,
        markTileScreenshotReady,
        markTileScreenshotErrored,
    ]);

    const userCanExportData = user.data?.ability.can(
        'manage',
        subject('ExportCsv', {
            organizationUuid: chartData?.organization.organizationUuid,
            projectUuid: chartData?.project.projectUuid,
        }),
    );

    // No chart available or savedSqlUuid is undefined - which means that the chart was deleted
    if (chartData === undefined || !savedSqlUuid) {
        const errorMessage = !savedSqlUuid
            ? 'This SQL chart has been deleted.'
            : chartError?.error?.message || 'Error fetching chart';

        const tileTitle =
            tile.properties.title ||
            tile.properties.chartName ||
            'Deleted SQL chart';

        return (
            <TileBase
                isEditMode={isEditMode}
                chartName={tile.properties.chartName ?? ''}
                tile={tile}
                isLoading={!!savedSqlUuid && isChartLoading}
                hasError
                title={tileTitle}
                chartKind={null}
                {...rest}
            >
                {(!savedSqlUuid || !isChartLoading) && (
                    <SuboptimalState
                        icon={IconAlertCircle}
                        title={tileTitle}
                        description={errorMessage}
                    />
                )}
            </TileBase>
        );
    }

    // Chart data but no results
    if (chartResultsData === undefined) {
        return (
            <TileBase
                isEditMode={isEditMode}
                chartName={tile.properties.chartName ?? ''}
                tile={tile}
                title={tile.properties.title || tile.properties.chartName || ''}
                isLoading={isChartResultsLoading}
                hasError={!!chartResultsError}
                chartKind={chartData.config.type}
                {...rest}
                titleHref={`/projects/${projectUuid}/sql-runner/${chartData.slug}`}
                extraMenuItems={
                    projectUuid &&
                    canManageSqlRunner &&
                    chartData.slug && (
                        <DashboardOptions
                            isEditMode={isEditMode}
                            projectUuid={projectUuid}
                            slug={chartData.slug}
                        />
                    )
                }
            >
                {chartResultsError && (
                    <SuboptimalState
                        icon={IconAlertCircle}
                        title={tile.properties.chartName}
                        description={
                            chartResultsError?.error?.message ||
                            'Error running query'
                        }
                    />
                )}
            </TileBase>
        );
    }

    // Chart available & results available!
    return (
        <TileBase
            isEditMode={isEditMode}
            chartName={tile.properties.chartName ?? ''}
            titleHref={`/projects/${projectUuid}/sql-runner/${chartData.slug}`}
            tile={tile}
            title={tile.properties.title || tile.properties.chartName || ''}
            chartKind={chartData.config.type}
            fullWidth={chartData.config.type === ChartKind.TABLE}
            {...rest}
            extraMenuItems={
                projectUuid &&
                (canManageSqlRunner || userCanExportData) && (
                    <>
                        {canManageSqlRunner && (
                            <DashboardOptions
                                isEditMode={isEditMode}
                                projectUuid={projectUuid}
                                slug={chartData.slug}
                            />
                        )}
                        <Menu.Item
                            leftSection={<MantineIcon icon={IconTableExport} />}
                            disabled={isEditMode}
                            onClick={() => setIsDataExportModalOpen(true)}
                        >
                            Download data
                        </Menu.Item>
                    </>
                )
            }
        >
            {chartData.config.type === ChartKind.TABLE &&
                isVizTableConfig(chartData.config) && (
                    // So that the Table tile isn't cropped by the overflow
                    <Box w="100%" h="100%" style={{ overflow: 'auto' }}>
                        <Table
                            resultsRunner={chartResultsData.resultsRunner}
                            columnsConfig={chartData.config.columns}
                            flexProps={{
                                mah: '100%',
                            }}
                        />
                    </Box>
                )}
            {(isVizCartesianChartConfig(chartData.config) ||
                isVizPieChartConfig(chartData.config)) && (
                <ChartView
                    config={chartData.config}
                    spec={specWithoutAnimation}
                    isLoading={isChartResultsFetching}
                    error={undefined}
                    style={{
                        minHeight: 'inherit',
                        height: '100%',
                        width: '100%',
                    }}
                />
            )}
            <ExportDataModal
                isOpen={isDataExportModalOpen}
                onClose={closeDataExportModal}
                projectUuid={projectUuid!}
                totalResults={
                    chartResultsData.chartUnderlyingData?.rows.length ?? 0
                }
                getDownloadQueryUuid={getDownloadQueryUuid}
                showTableNames
                chartName={tile.properties.title || chartData.name}
                columnOrder={chartResultsData.chartUnderlyingData?.columns}
            />
        </TileBase>
    );
};

export default SqlChartTile;
